import {
  faCaretDown,
  faCircleArrowDown,
  faCircleArrowRight,
  faCircleArrowUp,
  faCrown,
  faMapPin,
  faThumbtack,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, Fragment, SyntheticEvent, useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  DynamicHotelProps,
  GoToRoomDetailsParams,
  Hotel,
} from 'ts/hotel/interfaces/HotelResponse';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import { HotelFilterContext } from 'contexts/HotelFilterContext';
import { getCurrency, highlightStr } from 'utils/utils';
import HotelCardRooms from './HotelCardRooms';
import { MapHotelContext } from 'contexts/MapHotelContext';
import { useTranslation } from 'react-i18next';
import {
  getCenteredHotel,
  formatPriceHotel,
  formatPriceDynamic,
  getPriorityDesc,
  getOriginalHotelKey,
} from 'utils/hotel';
import { Price } from 'ts/hotel/interfaces/HotelDetails';
import HotelCardActionsPopup from './HotelCardActionsPopup';
import { useClickAway } from 'hooks/useClickAway';
import React from 'react';
import PreviewPreferredHotelModal from '../Modals/PreviewPreferredHotelModal';
import PreferredHotelModal from '../Modals/PreferredHotelModal';
import CancellationPolicyModal from '../Modals/CancellationPolicyModal';
import {
  clearLocalHotelCardAction,
  getLocalHotelCardAction,
  subscribeToLocalHotelCardAction,
} from 'utils/assistantChatStore';
import withErrorBoundary from 'components/shared/withErrorBoundary';

import GoogleHotelInfo from '../../TripadvisorGoogle/GoogleHotelInfo';
import { faDotCircle } from '@fortawesome/free-regular-svg-icons';
import HotelCardRooms2 from './HotelCardRooms2';
import { FacilityTreeItem } from 'services/hotel';
import HotelCardHotlist from './HotelCardHotlist';
import { MainContext } from 'contexts/MainContext';
import { b2b, imgBgImg, imgGlobe, b2c } from 'constants/generic';

interface HotelCardProps {
  hotel: Hotel;
  dynamic?: DynamicHotelProps;
  goToDetails: (params: GoToRoomDetailsParams) => void;
  hotlistFacilities?: FacilityTreeItem[];
}
const HotelCard: FC<HotelCardProps> = ({ hotel, dynamic, goToDetails, hotlistFacilities }) => {
  const { userSiteConfig, user } = useContext(MainContext);
  const [t] = useTranslation();
  const { filter } = useContext(HotelFilterContext);
  const {
    setCentered,
    setFocusedHotel,
    priceMode,
    currency,
    activeTab,
    setActiveTab,
    setActiveHotel,
    activeHotel,
    pinnedHotelsIds,
    setPinnedHotelsIds,
    setAreActiveRooms,
    areActiveRooms,
  } = useContext(MapHotelContext);
  const colorContrast = getComputedStyle(document.body).getPropertyValue('--color-contrast');
  const [showPrefferedModal, setShowPrefferedModal] = useState(false);
  const [showPreferredEditor, setShowPreferredEditor] = useState(false);
  const [refundRoomKey, setRefundRoomKey] = useState('');
  const localCardAction = useSyncExternalStore(
    subscribeToLocalHotelCardAction,
    getLocalHotelCardAction,
  );

  const isMobile = window.innerWidth < 768;
  const displayMode = userSiteConfig.hotel.displayMode;
  const isDesktopMode = !dynamic && !isMobile;
  const defaultAreRoomsFiltered = dynamic ? false : userSiteConfig.hotel.areRoomsFiltered;
  const areRoomsFiltered =
    user?.branch?.hotelDisplaySupplierName || dynamic ? defaultAreRoomsFiltered : true;
  const showRoomsInTab = isDesktopMode && userSiteConfig.hotel.showRoomsInTab;
  const isV4Mode = isDesktopMode;
  const isBusinessNew = isDesktopMode && [8, 9, 11, 12].includes(displayMode);
  const isPriceNew = isDesktopMode && [9, 12].includes(displayMode);
  const isV4Single = isDesktopMode && [7, 8, 9, 13].includes(displayMode);
  const isPriceAgent = isDesktopMode && [13, 14].includes(displayMode);
  const isSingleAgent = isDesktopMode && [15, 16, 17].includes(displayMode);
  const isRoomPerHotelMode = isDesktopMode && displayMode === 16;
  const isSingleAgentMulti = isDesktopMode && displayMode === 15;

  const cardRef = useRef<HTMLDivElement>(null);
  const visibleRoomClasses = hotel.roomClasses?.filter(
    (rc) => rc.visible && rc.visibleSm && (rc.visibleFiltered || !areRoomsFiltered)
  );
  const [showActions, setShowActions] = useState(false);
  const roomsMobile = document.querySelector('.hotel-result-settings') as HTMLElement;
  const popupRef = useClickAway(() => setShowActions(false));
  const [activeCardTab, setActiveCardTab] = useState(0);

  useEffect(() => {
    if (!showRoomsInTab && activeTab === 3) {
      setActiveTab(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRoomsInTab]);

  const onImageLoadError = ({ currentTarget }: SyntheticEvent<HTMLImageElement, Event>) => {
    currentTarget.onerror = null;
    currentTarget.src = imgBgImg;
  };

  const onClickCheapest = (rcKey: string, e: any) => {
    e.stopPropagation();

    setActiveHotel({ ...hotel, highlightedUk: rcKey });

    if (!showRoomsInTab) {
      setActiveCardTab(1);
    } else if (showRoomsInTab && userSiteConfig.hotel.displayStyle !== '2') {
      setActiveTab(3);
      roomsMobile?.classList.add('visible-responsive');
    } else if (showRoomsInTab && userSiteConfig.hotel.displayStyle === '2') {
      setAreActiveRooms?.(true);
    }
  };

  const onClickShowRooms = (forceShow?: boolean | null) => {
    if (!showRoomsInTab) {
      if (typeof forceShow === 'boolean') {
        setActiveCardTab(forceShow ? 1 : 0);
      } else {
        setActiveCardTab(activeCardTab === 1 ? 0 : 1);
      }
    } else if (showRoomsInTab && userSiteConfig.hotel.displayStyle !== '2') {
      setActiveTab(3);
      roomsMobile?.classList.add('visible-responsive');
    } else if (showRoomsInTab && userSiteConfig.hotel.displayStyle === '2') {
      if (typeof forceShow === 'boolean') {
        setAreActiveRooms?.(forceShow);
      } else {
        setAreActiveRooms?.(!areActiveRooms);
      }
    }
  };

  useEffect(() => {
    if (!localCardAction) {
      return;
    }
    if (localCardAction.hotelKey !== getOriginalHotelKey(hotel.htuKey)) {
      return;
    }

    if (localCardAction.kind === 'show-rooms') {
      onClickShowRooms(localCardAction.show);
    } else if (localCardAction.kind === 'open-preferred') {
      setShowPreferredEditor(true);
    } else if (localCardAction.kind === 'open-refund') {
      setRefundRoomKey(localCardAction.roomKey || hotel.roomClasses?.[0]?.rcUniqueKey || '');
    }

    clearLocalHotelCardAction();
  }, [localCardAction?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSelectHotel = () => {
    if (dynamic) return;
    if (![3, 5].includes(activeTab)) {
      setActiveTab(5);
    }
    roomsMobile?.classList.add('visible-responsive');
  };

  const onClickCenter = () => {
    setCentered(getCenteredHotel(hotel));
    setActiveTab(0);
  };

  const onClickFocus = () => {
    if (!hotel.position) return;
    setFocusedHotel({
      lat: hotel.position.latitude,
      lng: hotel.position.longitude,
      htuKey: hotel.htuKey,
      type: 'hotel',
      open: true,
      name: '',
    });
    setActiveTab(0);
  };

  const onClickPin = () => {
    const pinId = getOriginalHotelKey(hotel.htuKey);
    const ids = pinnedHotelsIds.includes(pinId)
      ? pinnedHotelsIds.filter((id) => id !== pinId)
      : [...pinnedHotelsIds, pinId];

    setPinnedHotelsIds(ids);
  };
  const active =
    activeHotel?.htuKey === hotel.htuKey && !dynamic && userSiteConfig.hotel.hotelCardStyle !== 1;
  const activeBorder =
    activeHotel?.htuKey === hotel.htuKey && !dynamic && userSiteConfig.hotel.hotelCardStyle === 1;

  return (
    <div
      className={
        'hotel-card-wrapper' +
        (activeCardTab ? ' open' : '') +
        (active ? ' active' : '') +
        (activeBorder ? ' active-border' : '') +
        (activeTab === 6 ? ' hotel-card-nomap' : '') +
        (b2b && !dynamic ? ' hotel-card-b2b' : '') +
        (isV4Mode ? ' hotel-card-v4' : '') +
        (isBusinessNew ? ' hotel-card-v8' : '') +
        (isSingleAgent ? ' hotel-card-v10' : '') +
        (isRoomPerHotelMode ? ' hotel-card-v16' : '')
      }
      id={hotel.htuKey}
      onClick={() => setActiveHotel(hotel)}
    >
      <div
        className="hotel-card hotel-card-cube"
        ref={cardRef}
      >
        {!dynamic && (
          <div className="hotel-card-pin cur-poi">
            <span className="crown-star">
              <Tooltip
                translateX={-10}
                content={t('hotel.pin_this_item')}
                children={<FontAwesomeIcon icon={faThumbtack} onClick={onClickPin} />}
              />
            </span>

            {user?.isAuth && (
              <div className="hotel-card-gt">
                <span className="position-relative ml-10">
                  <Tooltip content={t('WWW')}>
                    <div
                      className="display-flex"
                      onClick={() => setActiveCardTab(activeCardTab === (isV4Mode ? 1 : 3) ? 0 : (isV4Mode ? 1 : 3))}
                    >

                      <img
                        src={imgGlobe}
                        alt=""
                        className="mr-5"
                      />
                    </div>
                  </Tooltip>
                </span>
              </div>
            )}
          </div>
        )}
        <div className="hotel-card-img">
          {hotel.item.consumerRating > 0 && (
            <div className="hotel-consumer">
              <Tooltip
                content={t('hotel.rated_by_reviews', { rating: hotel.item.numberOfReviews })}
              >
                <div className="hotel-consumer-content">{hotel.item.consumerRating?.toFixed(1)}</div>
              </Tooltip>
            </div>
          )}

          <img
            className="cur-poi"
            src={hotel.info?.picture}
            alt={hotel?.name}
            onError={onImageLoadError}
            onClick={onSelectHotel}
          />
        </div>
        <div className="hotel-card-info">
          <Tooltip content={hotel.item.name} position="top">
            <div
              className={'title text-limited cur-poi ' + (hotel.similarHotel ? 'text-success' : '')}
              dangerouslySetInnerHTML={{
                __html: highlightStr(hotel.item.name, filter?.name || ''),
              }}
            />
          </Tooltip>

          <div className="address">
            <Tooltip
              position="top"
              content={t('hotel.center_me')}
              children={
                <FontAwesomeIcon
                  className="mr-3 cur-poi color-contrast"
                  icon={faDotCircle}
                  onClick={onClickCenter}
                />
              }
            />
            <span className="text-limited">{hotel.addressInfo?.address}</span>
            <div className="location-from ml-10">
              <Tooltip
                position="top"
                content={t('hotel.focus')}
                children={
                  <FontAwesomeIcon
                    style={{ marginLeft: 1.5, color: '#42c0fc' }}
                    className="mr-8 cur-poi"
                    icon={faMapPin}
                    onClick={onClickFocus}
                  />
                }
              />

              <span className="location-from-text text-limited">
                <span
                  className={
                    'text-bold' + (hotel.position?.pip === '1' ? ' text-success' : ' color-danger')
                  }
                >
                  {hotel.distance}
                  {t('km')}
                </span>
                <span> {t('from')} </span>
                <span className="text-bold">
                  {filter?.location.value?.name || hotel.position?.distanceText}
                </span>
              </span>
            </div>
          </div>
          {isBusinessNew && (
            <HotelCardHotlist hotel={hotel} hotlistFacilities={hotlistFacilities ?? []} />
          )}
          <div className="show-rooms" onClick={() => onClickShowRooms()}>
            <FontAwesomeIcon
              className="color-contrast mr-3"
              size="lg"
              icon={activeCardTab === 1 || areActiveRooms ? faCircleArrowUp : faCircleArrowDown}
            />
            <span>
              {t('hotel.rooms')}{' '}
              {
                hotel.roomClasses?.filter(
                  (room) => room.visible && (room.visibleFiltered || !areRoomsFiltered)
                )?.length
              }
            </span>
          </div>

          {hotel.item.numberOfReviews > 0 && (
            <div className="hotel-consumer-wr">
              <Tooltip
                content={t('hotel.rated_by_reviews', { rating: hotel.item.numberOfReviews })}
              >
                <div className="hotel-consumer-content">{hotel.item.consumerRating?.toFixed(1)}</div>
              </Tooltip>
            </div>
          )}
        </div>
        {!!visibleRoomClasses?.[0] && (
          <div className="hotel-card-total">

            <div className="display-flex">
              {!!hotel.serpResult && (
                <Tooltip
                  position="top"
                  children={

                    <img
                      src={imgGlobe}
                      alt=""
                      className="mr-5"
                    />
                  }
                  content={hotel.serpResult.lowest + hotel.serpResult.currency}
                />
              )}

              <Fragment>
                {hotel.priority?.internalBestVal ? (
                  <div className="crown mr-10" onClick={() => setShowPrefferedModal(true)}>
                    <Tooltip
                      content={t(getPriorityDesc(hotel.priority?.internalBestVal))}
                      position="bottom"
                      children={
                        <Fragment>
                          <span className="crown-text">
                            {t(getPriorityDesc(hotel.priority?.internalBestVal))?.[0]}
                          </span>
                          <FontAwesomeIcon icon={faCrown} />
                        </Fragment>
                      }
                    />
                  </div>
                ) : (
                  <div>
                    <div className="crown crown-empty">
                      <FontAwesomeIcon icon={faCrown} />
                    </div>
                  </div>
                )}
              </Fragment>

              <span className="stars-popup">
                <span
                  className={'stars-number ' + (b2c ? '' : 'cur-poi')}
                  onClick={() => !b2c && setShowActions(!showActions)}
                >
                  {hotel.item?.star || 0}
                </span>

                {!b2c ? (
                  <div>
                    <FontAwesomeIcon
                      icon={faCaretDown}
                      className="cur-poi"
                      onClick={() => setShowActions(!showActions)}
                    />
                    <HotelCardActionsPopup
                      showActions={showActions}
                      setShowActions={setShowActions}
                      hotel={hotel}
                      popupRef={popupRef}
                    />
                  </div>
                ) : (
                  <div className="mt-5"></div>
                )}
              </span>
            </div>

            <div className="price-wrapper">
              {!b2b && (
                <div className="flex-column" style={{ alignItems: 'flex-end' }}>
                  {!!dynamic && (
                    <div className="price">
                      <div className="text-xsm text-normal text-light mb-5">
                        {t('dyn.extra_money')}
                      </div>
                      <span>
                        {formatPriceDynamic(
                          visibleRoomClasses?.[0].price[dynamic.currency as keyof Price] /
                          dynamic.numberOfPax -
                          dynamic.choosenRoomPrice,
                          getCurrency(dynamic.currency)
                        )}
                      </span>
                    </div>
                  )}

                  {!dynamic && (
                    <div>
                      <span className="text-sm text-normal">{t('total')} </span>

                      <span
                        className="text-bold text-md"
                        dangerouslySetInnerHTML={{
                          __html: formatPriceHotel(
                            visibleRoomClasses?.[0].price,
                            currency,
                            priceMode
                          ),
                        }}
                      ></span>
                    </div>
                  )}
                  <FontAwesomeIcon
                    className={'cur-poi' + (dynamic?.isLoading ? ' icon-disabled' : '')}
                    icon={faCircleArrowRight}
                    color={colorContrast}
                    size="xl"
                    onClick={() => goToDetails({ room: visibleRoomClasses?.[0], hotel })}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {isV4Mode && (
        <div className="hotel-card-rooms-v4">
          <HotelCardRooms2
            rooms={hotel.roomClasses.filter((rc) => rc.visible)}
            hotel={{ ...hotel, highlightedUk: activeHotel?.highlightedUk }}
            goToDetails={goToDetails}
            showRoomsInTab={showRoomsInTab && !isSingleAgentMulti}
            onOpenRooms={onClickCheapest}
            roomsToPaint={isSingleAgent ? 1 : isBusinessNew ? 3 : 2}
            byCategoryMode={isPriceNew || isPriceAgent}
            isSingle={isV4Single || isSingleAgentMulti || isRoomPerHotelMode}
          />
        </div>
      )}

      {(activeCardTab === 3 || (isV4Mode && activeCardTab === 1)) && <div className="hotel-card-internet-v4"><GoogleHotelInfo hotel={hotel} /></div>}
      {activeCardTab === 1 && !showRoomsInTab && (
        <HotelCardRooms
          rooms={hotel.roomClasses}
          showRooms={activeCardTab === 1}
          hotel={{ ...hotel, highlightedUk: activeHotel?.highlightedUk }}
          dynamic={dynamic}
          goToDetails={goToDetails}
        />
      )}

      {showPrefferedModal && (
        <PreviewPreferredHotelModal hotel={hotel} onClose={() => setShowPrefferedModal(false)} />
      )}
      {showPreferredEditor && (
        <PreferredHotelModal hotel={hotel} onClose={() => setShowPreferredEditor(false)} />
      )}
      {!!refundRoomKey && (
        <CancellationPolicyModal
          hotel={hotel}
          rc={hotel.roomClasses.find((room) => room.rcUniqueKey === refundRoomKey) || hotel.roomClasses[0]}
          onClose={() => setRefundRoomKey('')}
        />
      )}
    </div>
  );
};

export default withErrorBoundary(React.memo(HotelCard), 'HotelCard');
